import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  inject,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { NgxPermissionsService } from 'ngx-permissions';
import { OrganisationService } from '@src/core/services';
import { OrganisationUI } from '@src/models';
import { BaseResizableComponent } from '@src/app/modules/base';
import { CustomNamesItem, CustomNamesService } from '@src/app/modules/custom-name-tabs';
import { OrganisationInfoStoreService, PermissionService } from '@src/app/modules/organisations/services';
import { NAVIGATION_ITEMS, NavigationItem, NavigationTree, ORGANISATION_NAVIGATION } from '@src/app/modules/navigation';
import { fixRouteParam } from '@src/utils';

import { AssociationMenuItem, AssociationMenuQueryId } from './constants';

@Component({
  selector: 'app-organisation',
  templateUrl: './organisation.component.html',
  styleUrls: ['./organisation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrganisationComponent extends BaseResizableComponent implements OnChanges {
  private readonly customNamesService = inject(CustomNamesService);
  private readonly route = inject(ActivatedRoute);
  private readonly organisationService = inject(OrganisationService);
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);
  protected readonly permissionService = inject(PermissionService);
  private readonly ngxPermissionsService = inject(NgxPermissionsService);
  private readonly organisationInfoStore = inject(OrganisationInfoStoreService);

  ASSOCIATION_MENU_ITEM = AssociationMenuItem;

  data?: OrganisationUI;

  @Input() organisationId?: string;
  @Output() deleted = new EventEmitter<string>();
  @Output() saved = new EventEmitter<void>();

  objectMenuWCustom$: BehaviorSubject<CustomNamesItem[] | undefined> = this.customNamesService.objectMenuWCustom$;
  loading: boolean = true;

  /** Объект, который был открыт в элементе меню */
  menuObjectId?: string;

  /** Текущий элемент меню */
  selectedItem?: NavigationItem;

  navMenuItems = NAVIGATION_ITEMS;
  navTree: NavigationTree[] = [];

  readonly organisationMenuItem = AssociationMenuItem;

  override ngOnInit(): void {
    super.ngOnInit();

    this.route.paramMap.pipe(takeUntil(this.destroyed$$)).subscribe(params => {
      const organisationId = fixRouteParam(params.get('id'));

      if (organisationId && organisationId !== this.organisationId) {
        this.organisationId = organisationId;
        this.onOrganisationIdChange();
      }

      this.cdr.markForCheck();
    });

    this.objectMenuWCustom$.pipe(takeUntil(this.destroyed$$)).subscribe(configs => {
      if (configs) {
        const permissions = this.ngxPermissionsService.getPermissions();

        this.navTree = ORGANISATION_NAVIGATION?.map(navItem => {
          const itemConfig = configs.find(
            config => config.codeName === navItem.itemName && config.languageCode === this.translateService.currentLang,
          );
          navItem.title = itemConfig?.title;

          return navItem;
        })
          .filter(navItem => {
            // Фильтрация по настройкам (visibilityOption)
            const itemConfig = configs.find(config => config.codeName === navItem.itemName);

            return !itemConfig || this.customNamesService.getVisibleMenuItem(itemConfig.visibilityOption);
          })
          .filter(navItem => {
            // Фильтрация по ролевой
            if (navItem.itemName === this.organisationMenuItem.Users) {
              return this.permissionService.allowUsersViewing$.value;
            }

            if (navItem.permissions?.length) {
              return navItem.permissions.find(permission => permission in permissions);
            }

            return true;
          });

        if (!this.selectedItem) {
          this.router.navigate([], {
            queryParams: { menu: AssociationMenuQueryId[this.navTree[0].itemName as AssociationMenuItem] },
            replaceUrl: true,
          });
        }

        this.cdr.markForCheck();
      }
    });

    this.route.queryParamMap.pipe(takeUntil(this.destroyed$$)).subscribe(query => {
      const menu = query.get('menu');
      const menuId = Object.entries(AssociationMenuQueryId).find(([, value]) => value === menu)?.[0];

      if (menuId) {
        this.selectedItem = this.getNavItemById(menuId);
      }

      this.menuObjectId = query.get('id') ?? undefined;

      this.cdr.markForCheck();
    });

    this.organisationInfoStore.data$.subscribe(() => {
      if (this.organisationId) {
        this.loading = false;
        this.data = this.organisationInfoStore.getData(this.organisationId);
        this.cdr.markForCheck();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.organisationId) {
      this.onOrganisationIdChange();
    }

    this.cdr.markForCheck();
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  onChangeMenuObjectId(id?: string) {
    this.menuObjectId = id;

    this.router.navigate([], {
      queryParams: {
        ...(id ? { id } : {}),
        menu: AssociationMenuQueryId[this.selectedItem?.id as AssociationMenuItem],
      },
    });
  }

  getMenuItemTitle(id: string): string | undefined {
    return this.navTree.find(item => item.itemName === id)?.title;
  }

  isMenuItemVisible(id?: string): boolean {
    return Boolean(id && this.navTree.find(item => item.itemName === id));
  }

  private getNavItemById(id: string) {
    return this.navMenuItems.find(menuItem => menuItem.id === id);
  }

  private onOrganisationIdChange() {
    this.loading = true;
    this.selectedItem = undefined;
    this.router.navigate([], { queryParams: {}, replaceUrl: true });
    this.navTree = [];
    this.customNamesService.resetObjectMenuWCustom();

    if (this.organisationId) {
      this.organisationService.getOrganisationNew(this.organisationId, true).subscribe();
      this.permissionService.getPermissions(this.organisationId);
    }

    this.cdr.markForCheck();
  }
}
